/**
 * ZoomableImage
 *
 * Shared "+" hover hint + full-screen lightbox with a hover magnifier lens
 * (Amazon-style: moving the mouse over the image shows a zoomed panel
 * beside it). Used anywhere a thumbnail needs to zoom to full screen:
 * EventDetail Info tab hero image, EventDetail Images tab, and the
 * Flyer History sub-tabs (Venue/Artist/Promoter/Festival).
 *
 * Exports:
 *   ZoomHint          — small "+" overlay icon; parent controls visibility via `visible`
 *   ImageLightbox      — full-screen modal; props: src, alt, caption, viewShowTo,
 *                         viewLinkLabel, onClose, onEdit (optional — renders an
 *                         Edit button under the magnifier hint on desktop, or in
 *                         the footer on touch devices, when provided),
 *                         imageMeta (optional — an image row from getImages; when
 *                         it carries photo_artist_name / credit_artist_name the
 *                         footer shows "Pictured:" / "Credit:" artist links —
 *                         2026-10-12 Visual Artists & Image Credits).
 *                         When imageMeta doesn't carry credit fields (e.g. a
 *                         Flyer History show flyer opened from its URL), the
 *                         lightbox looks them up itself via the public
 *                         GET /images/credit?url=… (getImageCredit.js).
 *                         Desktop: shown under "Hover the image to zoom in";
 *                         touch: shown in the footer.
 *   ImageQuickEditModal — lightweight Caption / Visibility / Save / Delete form
 *                         for entity-record photo tabs (Event/Artist/Venue/
 *                         Festival/Promoter/Member). No entity reassignment here
 *                         — that flow only lives in My Images. Props: image,
 *                         onClose, onSaved(updatedImage), onDeleted(imageId)
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

const PANEL_SIZE = 420;   // px, magnifier panel dimensions
const ZOOM = 2.5;         // magnification factor

// ── "+" hover hint icon ────────────────────────────────────────────────────
export function ZoomHint({ visible = true, style }) {
    return (
        <div style={{ ...S.zoomHint, opacity: visible ? 1 : 0, ...style }}>
            <span style={S.zoomHintIcon}>+</span>
        </div>
    );
}

// ── Full-screen lightbox with magnifier ────────────────────────────────────
export function ImageLightbox({ src, alt, caption, viewShowTo, viewLinkLabel = 'View Show →', onClose, onEdit, imageMeta }) {
    const [isTouch] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches
    );
    const [lens, setLens] = useState(null); // { boxLeft, boxTop, boxSize, bgX, bgY, bgW, bgH }
    const imgRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleMouseMove = (e) => {
        if (isTouch || !imgRef.current) return;
        const rect = imgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
            setLens(null);
            return;
        }
        const boxSize = PANEL_SIZE / ZOOM;
        const boxLeft = Math.min(Math.max(x - boxSize / 2, 0), rect.width - boxSize);
        const boxTop  = Math.min(Math.max(y - boxSize / 2, 0), rect.height - boxSize);
        setLens({
            boxLeft, boxTop, boxSize,
            bgX: -(boxLeft * ZOOM),
            bgY: -(boxTop * ZOOM),
            bgW: rect.width * ZOOM,
            bgH: rect.height * ZOOM,
        });
    };

    const showMagnifier = !isTouch;

    // Pictured / credited artist lines (2026-10-12). Image rows from
    // getImages carry credit_artist_name (null or a name); anything else is
    // looked up by URL.
    const metaHasCredit = !!imageMeta && Object.prototype.hasOwnProperty.call(imageMeta, 'credit_artist_name');
    const [lookedUp, setLookedUp] = useState(null); // { credit, pictured } from GET /images/credit
    useEffect(() => {
        setLookedUp(null);
        if (metaHasCredit || !src || !/^https?:\/\//i.test(src)) return;
        let cancelled = false;
        axios.get(`${config.API_URL}/images/credit`, { params: { url: src } })
            .then(res => { if (!cancelled) setLookedUp(res.data || null); })
            .catch(() => { /* no credit line — never block the lightbox */ });
        return () => { cancelled = true; };
    }, [src, metaHasCredit]);

    const pictured = imageMeta?.photo_artist_name
        ? { name: imageMeta.photo_artist_name, slug: imageMeta.photo_artist_slug || imageMeta.photo_artist_id }
        : (lookedUp?.pictured ? { name: lookedUp.pictured.name, slug: lookedUp.pictured.slug || lookedUp.pictured.id } : null);
    const credit = metaHasCredit
        ? (imageMeta.credit_artist_name ? { name: imageMeta.credit_artist_name, slug: imageMeta.credit_artist_slug || imageMeta.credit_artist_id } : null)
        : (lookedUp?.credit ? { name: lookedUp.credit.name, slug: lookedUp.credit.slug || lookedUp.credit.id } : null);

    const creditLines = (rowStyle) => (pictured || credit) && (
        <div style={rowStyle}>
            {pictured && (
                <span>Pictured: <Link to={`/artists/${pictured.slug}`} style={S.creditLink} onClick={onClose}>{pictured.name}</Link></span>
            )}
            {pictured && credit && <span style={S.creditSep}>·</span>}
            {credit && (
                <span>Credit: <Link to={`/artists/${credit.slug}`} style={S.creditLink} onClick={onClose}>{credit.name}</Link></span>
            )}
        </div>
    );

    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.stage} onClick={(e) => e.stopPropagation()}>
                <div style={{ ...S.imageCol, ...(showMagnifier ? {} : S.imageColTouch) }}>
                    <div
                        style={S.imageWrap}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={() => setLens(null)}
                    >
                        <img
                            ref={imgRef}
                            src={src}
                            alt={alt || ''}
                            style={showMagnifier ? S.imgDesktop : S.imgTouch}
                        />
                        {showMagnifier && lens && (
                            <div
                                style={{
                                    ...S.lensBox,
                                    left: lens.boxLeft,
                                    top: lens.boxTop,
                                    width: lens.boxSize,
                                    height: lens.boxSize,
                                }}
                            />
                        )}
                    </div>
                    {(caption || viewShowTo || (!showMagnifier && (pictured || credit)) || (onEdit && !showMagnifier)) && (
                        <div style={S.footer}>
                            {caption && <div style={S.caption}>{caption}</div>}
                            {/* Touch devices have no magnifier panel — credit goes in the footer */}
                            {!showMagnifier && creditLines(S.creditRow)}
                            {viewShowTo && (
                                <Link to={viewShowTo} style={S.viewShowLink} onClick={onClose}>
                                    {viewLinkLabel}
                                </Link>
                            )}
                            {onEdit && !showMagnifier && (
                                <button onClick={onEdit} style={S.footerEditBtn}>✏️ Edit</button>
                            )}
                        </div>
                    )}
                </div>

                {showMagnifier && (
                    <div
                        style={{
                            ...S.magnifierPanel,
                            ...(lens
                                ? {
                                    backgroundImage: `url(${src})`,
                                    backgroundSize: `${lens.bgW}px ${lens.bgH}px`,
                                    backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
                                    backgroundRepeat: 'no-repeat',
                                }
                                : {}),
                        }}
                    >
                        {!lens && (
                            <div style={S.magnifierHintWrap}>
                                <div style={S.magnifierHint}>Hover the image to zoom in</div>
                                {creditLines(S.hintCreditRow)}
                                {onEdit && <button onClick={onEdit} style={S.editBtn}>✏️ Edit</button>}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <button onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>
    );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const S = {
    zoomHint: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '26px',
        height: '26px',
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.6)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.15s ease',
        pointerEvents: 'none',
        zIndex: 2,
    },
    zoomHintIcon: { fontSize: '15px', fontWeight: '700', lineHeight: 1 },

    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000, padding: '24px',
    },
    stage: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start',
        justifyContent: 'center', gap: '20px', maxWidth: '95vw', maxHeight: '90vh',
    },
    imageCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 },
    imageColTouch: { maxWidth: '90vw' },
    imageWrap: { position: 'relative', display: 'inline-block', cursor: 'crosshair' },
    imgDesktop: { display: 'block', maxWidth: 'min(50vw, 520px)', maxHeight: '78vh', objectFit: 'contain', borderRadius: '8px' },
    imgTouch: { display: 'block', maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' },
    lensBox: {
        position: 'absolute', border: '2px solid #bb86fc', background: 'rgba(187,134,252,0.15)',
        pointerEvents: 'none', borderRadius: '2px',
    },
    magnifierPanel: {
        width: `${PANEL_SIZE}px`, height: `${PANEL_SIZE}px`, flexShrink: 0,
        background: '#1a1a1a', border: '1px solid #333', borderRadius: '8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    magnifierHintWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
    magnifierHint: { color: '#666', fontSize: '13px', textAlign: 'center', padding: '0 24px' },
    editBtn: {
        background: 'transparent', border: '1px solid #bb86fc', borderRadius: '6px',
        color: '#bb86fc', padding: '6px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    },
    footerEditBtn: {
        marginTop: '8px', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '6px',
        color: '#bb86fc', padding: '6px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
    },

    footer: { marginTop: '10px', textAlign: 'center' },
    caption: { color: '#e0e0e0', fontSize: '13px', marginBottom: '4px' },
    creditRow: { color: '#999', fontSize: '12px', marginBottom: '8px', display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' },
    hintCreditRow: { color: '#999', fontSize: '13px', display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', padding: '0 24px', textAlign: 'center' },
    creditLink: { color: '#bb86fc', textDecoration: 'none' },
    creditSep: { color: '#555' },
    viewShowLink: {
        display: 'inline-block', color: '#bb86fc', fontSize: '13px', fontWeight: '600',
        textDecoration: 'none', border: '1px solid #bb86fc', borderRadius: '6px', padding: '6px 14px',
    },

    closeBtn: {
        position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.6)',
        border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', borderRadius: '6px',
        padding: '4px 12px', zIndex: 3001,
    },
};

// ── Quick Edit Modal (Caption / Visibility / Save / Delete) ────────────────
//
// Used from entity-record photo tabs (Event/Artist/Venue/Festival/Promoter/
// Member), reached via the Edit button rendered by ImageLightbox above.
// Unlike MyImages.js's EditModal, this never offers "Change Assignment" —
// an image opened from a record's own photo tab is always fixed to that
// record; reassignment is a My Images-only flow.
//
// Props:
//   image      — { id, caption, is_public, image_url }
//   onClose    — called after a successful Save/Delete, or on X / backdrop click
//   onSaved    — (updatedImage) => void, called after a successful PATCH
//   onDeleted  — (imageId) => void, called after a successful DELETE
export function ImageQuickEditModal({ image, onClose, onSaved, onDeleted }) {
    const [caption, setCaption] = useState(image.caption || '');
    const [isPublic, setIsPublic] = useState(image.is_public !== false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const token = await getIdToken();
            const res = await axios.patch(`${config.API_URL}/images/${image.id}`, {
                caption: caption.trim() || null,
                isPublic,
            }, { headers: { Authorization: token } });
            onSaved(res.data.image);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Save failed.');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        setDeleting(true); setError('');
        try {
            const token = await getIdToken();
            await axios.delete(`${config.API_URL}/images/${image.id}`, { headers: { Authorization: token } });
            onDeleted(image.id);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Delete failed.');
        } finally { setDeleting(false); }
    };

    return (
        <div style={QS.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={QS.modal}>
                <div style={QS.modalHeader}>
                    <span style={QS.title}>Edit Photo</span>
                    <button onClick={onClose} style={QS.closeBtn}>✕</button>
                </div>

                <div style={QS.twoCol}>
                    <div style={QS.twoColLeft}>
                        <img src={image.image_url} alt={caption || ''} style={QS.previewImg} />
                    </div>
                    <div style={QS.twoColRight}>
                        <div>
                            <label style={QS.label}>Caption (optional)</label>
                            <input
                                type="text"
                                value={caption}
                                onChange={e => setCaption(e.target.value)}
                                placeholder="Describe this photo..."
                                style={QS.input}
                                maxLength={280}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label style={{ ...QS.label, margin: 0 }}>Visibility</label>
                            <button
                                onClick={() => setIsPublic(v => !v)}
                                style={{ ...QS.toggleBtn, backgroundColor: isPublic ? '#2d6a4f' : '#444', borderColor: isPublic ? '#40916c' : '#555' }}
                            >
                                {isPublic ? '🌐 Public' : '🔒 Private'}
                            </button>
                            <span style={{ fontSize: '12px', color: '#888' }}>{isPublic ? 'Visible on the entity page' : 'Only visible to you'}</span>
                        </div>
                        {error && <div style={QS.errorMsg}>{error}</div>}
                        <button onClick={handleSave} disabled={saving} style={{ ...QS.primaryBtn, opacity: saving ? 0.6 : 1 }}>
                            {saving ? 'Saving…' : '💾 Save Changes'}
                        </button>
                        <div style={QS.deleteRow}>
                            {!confirmDelete ? (
                                <button onClick={() => setConfirmDelete(true)} style={QS.deleteBtn}>🗑️ Delete Photo</button>
                            ) : (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '13px', color: '#e57373' }}>Delete permanently?</span>
                                    <button onClick={handleDelete} disabled={deleting} style={{ ...QS.deleteBtn, opacity: deleting ? 0.6 : 1 }}>
                                        {deleting ? 'Deleting…' : 'Yes, Delete'}
                                    </button>
                                    <button onClick={() => setConfirmDelete(false)} style={QS.ghostBtn}>Cancel</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Quick Edit Modal styles ──────────────────────────────────────────────────
const QS = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3500, padding: '16px',
    },
    modal: {
        background: '#1e1e1e', border: '1px solid #333', borderRadius: '12px',
        padding: '24px', width: '100%', maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto',
    },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title: { fontSize: '18px', fontWeight: 600, color: '#e0e0e0' },
    closeBtn: { background: 'transparent', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' },
    twoColLeft: { display: 'flex', flexDirection: 'column' },
    twoColRight: { display: 'flex', flexDirection: 'column', gap: '14px' },
    previewImg: { width: '100%', maxHeight: '380px', borderRadius: '8px', objectFit: 'contain', background: '#111', display: 'block' },
    label: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { width: '100%', background: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box' },
    toggleBtn: { border: '1px solid', borderRadius: '20px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', color: '#e0e0e0' },
    primaryBtn: { background: '#bb86fc', color: '#1a1a1a', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', width: '100%' },
    ghostBtn: { background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    deleteBtn: { background: 'transparent', border: '1px solid #e57373', borderRadius: '6px', color: '#e57373', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    deleteRow: { borderTop: '1px solid #333', paddingTop: '12px', marginTop: '4px' },
    errorMsg: { color: '#e57373', background: '#2a1a1a', border: '1px solid #6b2c2c', borderRadius: '6px', padding: '8px 12px', fontSize: '13px' },
};
